/** Moving points and whole objects (TD §5: moveVert, moveNode, moveIcon, moveObject). */
import type { AreaId, IconId, NodeId, PlanDoc, RiverId, RoadId, VertId } from '../../shared/types';
import { poolsOf, ringsUsingVert } from '../model/query';
import { ok, refuse, type OpResult } from './result';
import { addVert, clampToMap } from './util';

/** Moves one corner. Every area sharing it follows, because they share the id. Clamped to the map. */
export function moveVert(draft: PlanDoc, args: { vertId: VertId; x: number; y: number }): void {
  const v = draft.verts[args.vertId];
  if (!v) return;
  [v.x, v.y] = clampToMap(draft, args.x, args.y);
}

/** Moves one road dot. Every road using it (a junction) follows. Clamped to the map. */
export function moveNode(draft: PlanDoc, args: { nodeId: NodeId; x: number; y: number }): void {
  const n = draft.nodes[args.nodeId];
  if (!n) return;
  [n.x, n.y] = clampToMap(draft, args.x, args.y);
}

/** Moves one icon. Clamped to the map. */
export function moveIcon(draft: PlanDoc, args: { iconId: IconId; x: number; y: number }): void {
  const ic = draft.icons[args.iconId];
  if (!ic) return;
  [ic.x, ic.y] = clampToMap(draft, args.x, args.y);
}

/**
 * Gives the areas in `areaIds` their own copies of every corner they share with a ring outside that set,
 * so moving them doesn't drag neighbours out of shape (R11). Copies keep position and pin, so shapes don't change.
 * Returns the number of corners copied.
 */
export function detachAreas(draft: PlanDoc, areaIds: readonly AreaId[]): number {
  const inSet = new Set(areaIds);
  const copies = new Map<VertId, VertId>();
  for (const aid of areaIds) {
    const a = draft.areas[aid];
    if (!a) continue;
    const next = a.verts.map(vid => {
      const known = copies.get(vid);
      if (known) return known;
      const outside = ringsUsingVert(draft, vid).some(r => r === 'plot' || !inSet.has(r.area));
      const v = draft.verts[vid];
      if (!outside || !v) return vid;
      const c = addVert(draft, v.x, v.y, v.pinned);
      copies.set(vid, c);
      return c;
    });
    if (next.some((v, i) => v !== a.verts[i])) a.verts = next;
  }
  return copies.size;
}

/** What moveObject moves. */
export type ObjectRef =
  | { kind: 'area'; id: AreaId }
  | { kind: 'road'; id: RoadId }
  | { kind: 'river'; id: RiverId }
  | { kind: 'icon'; id: IconId };

/**
 * Moves a whole object by (dx, dy).
 * - An area first gets its own copies of shared corners (R11); its pools move with it.
 * - A road moves all its dots, including junction dots, so attached roads stay attached.
 * Not clamped, so a drag can be undone by the opposite move.
 */
export function moveObject(draft: PlanDoc, args: { target: ObjectRef; dx: number; dy: number }): OpResult {
  const { target: t, dx, dy } = args;
  if (t.kind === 'area') {
    if (!draft.areas[t.id]) return refuse('That area no longer exists.');
    const set = [t.id, ...poolsOf(draft, t.id)];
    detachAreas(draft, set);
    const moved = new Set<VertId>();
    for (const aid of set) for (const vid of draft.areas[aid]?.verts ?? []) moved.add(vid);
    for (const vid of moved) { const v = draft.verts[vid]; if (v) { v.x += dx; v.y += dy; } }
  } else if (t.kind === 'road') {
    const r = draft.roads[t.id];
    if (!r) return refuse('That road no longer exists.');
    for (const nid of new Set(r.nodes)) { const n = draft.nodes[nid]; if (n) { n.x += dx; n.y += dy; } }
  } else if (t.kind === 'river') {
    const rv = draft.rivers[t.id];
    if (!rv) return refuse('That river no longer exists.');
    for (const p of rv.pts) { p[0] += dx; p[1] += dy; }
  } else {
    const ic = draft.icons[t.id];
    if (!ic) return refuse('That icon no longer exists.');
    ic.x += dx; ic.y += dy;
  }
  return ok(true);
}
